import type { IPascoRepository } from "@src/domain/pascos/repository/pasco_repository";
import type { PascoDTO, PascoListItemDTO, UpdatePascoDTO, ExtractedMetadataDTO } from "../dto";
import type { CreatePascoData } from "@src/domain/pascos/value_objects/create_pasco_data";
import type { StudentUploadPascoData } from "@src/domain/pascos/value_objects/student_upload_pasco_data";
import type { PresignedUrl } from "@src/domain/pascos/value_objects/presigned_url";
import type { PreviewUrl } from "@src/domain/pascos/value_objects/preview_url";

import { ListAllPascosUseCase } from "./list_all_pascos.usecase";
import { GetPascoByIdUseCase } from "./get_pasco_by_id.usecase";
import { CreatePascoUseCase } from "./create_pasco.usecase";
import { StudentUploadPascoUseCase } from "./student_upload_pasco.usecase";
import { UpdatePascoUseCase } from "./update_pasco.usecase";
import { DeletePascoUseCase } from "./delete_pasco.usecase";
import { ApprovePascoUseCase } from "./approve_pasco.usecase";
import { ReviewPascoUseCase } from "./review_pasco.usecase";
import { ScheduleReviewPascoUseCase } from "./schedule_review_pasco.usecase";
import { RunPascoPipelineUseCase } from "./run_pasco_pipeline.usecase";
import { GetPascoDownloadUrlUseCase } from "./get_pasco_download_url.usecase";
import { DownloadPascoStreamUseCase } from "./download_pasco_stream.usecase";
import { GetPascoPreviewUrlUseCase } from "./get_pasco_preview_url.usecase";
import { GetPendingReviewPascosUseCase } from "./get_pending_review_pascos.usecase";
import { GetPendingApprovalPascosUseCase } from "./get_pending_approval_pascos.usecase";
import { ExtractPascoMetadataUseCase } from "./extract_pasco_metadata.usecase";

/**
 * PASCO Service
 * Consolidated service that provides access to all PASCO-related use cases
 * This class serves as a facade pattern to simplify usage of use cases
 * 
 * IMPORTANT: This service only exposes DTOs to the presentation layer,
 * ensuring proper separation between domain and presentation concerns.
 */
export class PascoService {
    // Repository reference
    private readonly pascoRepository: IPascoRepository;

    // Use case instances
    private readonly listAllPascosUseCase: ListAllPascosUseCase;
    private readonly getPascoByIdUseCase: GetPascoByIdUseCase;
    private readonly createPascoUseCase: CreatePascoUseCase;
    private readonly studentUploadPascoUseCase: StudentUploadPascoUseCase;
    private readonly updatePascoUseCase: UpdatePascoUseCase;
    private readonly deletePascoUseCase: DeletePascoUseCase;
    private readonly approvePascoUseCase: ApprovePascoUseCase;
    private readonly reviewPascoUseCase: ReviewPascoUseCase;
    private readonly scheduleReviewPascoUseCase: ScheduleReviewPascoUseCase;
    private readonly runPascoPipelineUseCase: RunPascoPipelineUseCase;
    private readonly getPascoDownloadUrlUseCase: GetPascoDownloadUrlUseCase;
    private readonly downloadPascoStreamUseCase: DownloadPascoStreamUseCase;
    private readonly getPascoPreviewUrlUseCase: GetPascoPreviewUrlUseCase;
    private readonly getPendingReviewPascosUseCase: GetPendingReviewPascosUseCase;
    private readonly getPendingApprovalPascosUseCase: GetPendingApprovalPascosUseCase;
    private readonly extractPascoMetadataUseCase: ExtractPascoMetadataUseCase;

    constructor(pascoRepository: IPascoRepository) {
        // Store repository reference
        this.pascoRepository = pascoRepository;

        // Initialize all use cases with the repository
        this.listAllPascosUseCase = new ListAllPascosUseCase(pascoRepository);
        this.getPascoByIdUseCase = new GetPascoByIdUseCase(pascoRepository);
        this.studentUploadPascoUseCase = new StudentUploadPascoUseCase(pascoRepository);
        this.createPascoUseCase = new CreatePascoUseCase(pascoRepository);
        this.updatePascoUseCase = new UpdatePascoUseCase(pascoRepository);
        this.deletePascoUseCase = new DeletePascoUseCase(pascoRepository);
        this.approvePascoUseCase = new ApprovePascoUseCase(pascoRepository);
        this.reviewPascoUseCase = new ReviewPascoUseCase(pascoRepository);
        this.scheduleReviewPascoUseCase = new ScheduleReviewPascoUseCase(pascoRepository);
        this.runPascoPipelineUseCase = new RunPascoPipelineUseCase(pascoRepository);
        this.getPascoDownloadUrlUseCase = new GetPascoDownloadUrlUseCase(pascoRepository);
        this.downloadPascoStreamUseCase = new DownloadPascoStreamUseCase(pascoRepository);
        this.getPascoPreviewUrlUseCase = new GetPascoPreviewUrlUseCase(pascoRepository);
        this.getPendingReviewPascosUseCase = new GetPendingReviewPascosUseCase(pascoRepository);
        this.getPendingApprovalPascosUseCase = new GetPendingApprovalPascosUseCase(pascoRepository);
        this.extractPascoMetadataUseCase = new ExtractPascoMetadataUseCase(pascoRepository);
    }

    /**
     * List all PASCOs
     * @returns Promise<PascoListItemDTO[]> - Array of PASCO DTOs optimized for list display
     */
    async listAll(): Promise<PascoListItemDTO[]> {
        return await this.listAllPascosUseCase.execute();
    }

    /**
     * List PASCOs with pagination and filtering
     * @param params - Filter and pagination parameters
     * @returns Promise with paginated result containing PASCOs and metadata
     */
    async listPaginated(params?: {
        q?: string;
        status?: string;
        skip?: number;
        limit?: number;
    }): Promise<{ pascos: PascoListItemDTO[]; total: number; hasMore: boolean }> {
        const result = await this.pascoRepository.listPaginated({
            q: params?.q,
            status: params?.status,
            skip: params?.skip,
            limit: params?.limit,
        });

        // Import mapper at runtime to avoid circular dependency
        const { PascoDtoMapper } = await import('../mappers/pasco-dto.mapper');

        return {
            pascos: PascoDtoMapper.toListItemDTOArray(result.pascos),
            total: result.total,
            hasMore: result.hasMore,
        };
    }

    /**
     * Get a PASCO by ID
     * @param pascoId - The unique identifier of the PASCO
     * @returns Promise<PascoDTO | null> - The PASCO DTO or null if not found
     */
    async getById(pascoId: string): Promise<PascoDTO | null> {
        return await this.getPascoByIdUseCase.execute(pascoId);
    }

    /**
     * Create a new PASCO
     * @param data - The PASCO creation data including file and metadata
     * @param onUploadProgress - Optional callback for upload progress
     * @returns Promise<PascoDTO> - The created PASCO DTO
     */
    async create(
        data: CreatePascoData,
        onUploadProgress?: (progress: number) => void
    ): Promise<PascoDTO> {
        return await this.createPascoUseCase.execute(data, onUploadProgress);
    }

    /**Student upload PASCO
     * Allows students to upload PASCO files with optional university selection
     * @param data - The student upload data (file and optional university_id)
     * @param onUploadProgress - Optional callback for upload progress
     * @returns Promise<PascoDTO> - The uploaded PASCO DTO
     */
    async studentUpload(
        data: StudentUploadPascoData,
        onUploadProgress?: (progress: number) => void
    ): Promise<PascoDTO> {
        return await this.studentUploadPascoUseCase.execute(data, onUploadProgress);
    }

    /**
     * 
     * Update an existing PASCO
     * @param updateData - The update data containing PASCO ID and fields to update
     * @returns Promise<PascoDTO> - The updated PASCO DTO
     */
    async update(updateData: UpdatePascoDTO): Promise<PascoDTO> {
        return await this.updatePascoUseCase.execute(updateData);
    }

    /**
     * Delete a PASCO
     * @param pascoId - The unique identifier of the PASCO to delete
     * @returns Promise<void>
     */
    async delete(pascoId: string): Promise<void> {
        return await this.deletePascoUseCase.execute(pascoId);
    }

    /**
     * Approve a PASCO
     * @param pascoId - The unique identifier of the PASCO to approve
     * @returns Promise<PascoDTO> - The approved PASCO DTO
     */
    async approve(pascoId: string): Promise<PascoDTO> {
        return await this.approvePascoUseCase.execute(pascoId);
    }

    /**
     * Review a PASCO
     * @param pascoId - The unique identifier of the PASCO to review
     * @returns Promise<PascoDTO> - The reviewed PASCO DTO
     */
    async review(pascoId: string): Promise<PascoDTO> {
        return await this.reviewPascoUseCase.execute(pascoId);
    }

    /**
     * Schedule a PASCO for review
     * @param pascoId - The unique identifier of the PASCO to schedule for review
     * @returns Promise<PascoDTO> - The PASCO DTO marked for review
     */
    async scheduleReview(pascoId: string): Promise<PascoDTO> {
        return await this.scheduleReviewPascoUseCase.execute(pascoId);
    }

    /**
     * Get pending review PASCOs
     * @param params - Filter and pagination parameters
     * @returns Promise<{ pascos: PascoListItemDTO[]; total: number; hasMore: boolean }> - Paginated pending review PASCOs
     */
    async getPendingReviewPascos(params?: {
        q?: string;
        university_id?: string;
        semester?: string;
        academic_year?: string;
        skip?: number;
        limit?: number;
    }): Promise<{ pascos: PascoListItemDTO[]; total: number; hasMore: boolean }> {
        return await this.getPendingReviewPascosUseCase.execute(params);
    }

    /**
     * Get pending approval PASCOs
     * @param params - Filter and pagination parameters
     * @returns Promise<{ pascos: PascoListItemDTO[]; total: number; hasMore: boolean }> - Paginated pending approval PASCOs
     */
    async getPendingApprovalPascos(params?: {
        q?: string;
        university_id?: string;
        semester?: string;
        academic_year?: string;
        skip?: number;
        limit?: number;
    }): Promise<{ pascos: PascoListItemDTO[]; total: number; hasMore: boolean }> {
        return await this.getPendingApprovalPascosUseCase.execute(params);
    }

    /**
     * Run the PASCO processing pipeline
     * @returns Promise<void>
     */
    async runPipeline(): Promise<void> {
        return await this.runPascoPipelineUseCase.execute();
    }

    /**
     * Get download URL for a PASCO file
     * @param pascoId - The unique identifier of the PASCO
     * @returns Promise<PresignedUrl> - The presigned URL object with download URL and expiration
     */
    async getDownloadUrl(pascoId: string): Promise<PresignedUrl> {
        return await this.getPascoDownloadUrlUseCase.execute(pascoId);
    }

    /**
     * Download PASCO file as blob stream
     * @param pascoId - The unique identifier of the PASCO
     * @returns Promise<Blob> - The PASCO file as a binary blob
     */
    async downloadStream(pascoId: string): Promise<Blob> {
        return await this.downloadPascoStreamUseCase.execute(pascoId);
    }

    /**
     * Get preview URL for a PASCO file (JPG image)
     * @param pascoId - The unique identifier of the PASCO
     * @returns Promise<PreviewUrl> - The preview URL object with preview URL and expiration
     */
    async getPreviewUrl(pascoId: string): Promise<PreviewUrl> {
        return await this.getPascoPreviewUrlUseCase.execute(pascoId);
    }

    /**
     * Extract metadata from a raw PASCO using AI
     * Used during admin approval workflow when metadata is missing
     * @param pascoId - The unique identifier of the PASCO
     * @returns Promise<ExtractedMetadataDTO> - The extracted metadata
     */
    async extractMetadata(pascoId: string): Promise<ExtractedMetadataDTO> {
        return await this.extractPascoMetadataUseCase.execute(pascoId);
    }

    // Provide access to individual use cases if needed for advanced scenarios
    get useCases() {
        return {
            listAll: this.listAllPascosUseCase,
            getById: this.getPascoByIdUseCase,
            create: this.createPascoUseCase,
            update: this.updatePascoUseCase,
            delete: this.deletePascoUseCase,
            approve: this.approvePascoUseCase,
            runPipeline: this.runPascoPipelineUseCase,
            getDownloadUrl: this.getPascoDownloadUrlUseCase,
        };
    }
}
